import * as ToggleGroup from "@radix-ui/react-toggle-group";
import React from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "~/components/button";
import { TextArea } from "~/components/form/text-area";
import { Icon } from "~/components/icon";
import { useClickOutside } from "~/lib/hooks/use-click-outside";
import { cn } from "~/lib/utils";
import { useFeedback } from "~/mutations/use-feedback";

export const Feedback = () => {
  const [open, setOpen] = React.useState<boolean>(false);
  const contentRef = React.useRef<HTMLTextAreaElement>(null);
  const childRef = React.useRef<HTMLDivElement>(null);
  const feedbackRef = React.useRef<HTMLDivElement>(null);

  // TODO: remove manual state control for rating
  const [rating, setRating] = React.useState<string>("");

  useClickOutside(feedbackRef, () => setOpen(false));

  const { t } = useTranslation(["feedback"]);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = useForm<{ content: string; rating: string }>();

  const { mutate, status } = useFeedback({
    onSuccess: () => {
      setOpen(false);
      resetFeedback();
    },
    onError: () => {
      setOpen(false);
      resetFeedback();
    },
  });

  const resetFeedback = () => {
    setRating("");
    reset();
  };

  const submitFeedback: SubmitHandler<{ content: string; rating: string }> = (
    data,
  ) => {
    mutate({ ...data, url: window.location.href });
  };

  React.useEffect(() => {
    if (open) {
      if (contentRef.current) contentRef.current.focus();
      window.scrollTo({
        top: document.documentElement.scrollHeight - window.innerHeight,
        behavior: "smooth",
      });
    } else {
      resetFeedback();
    }
  }, [open]);

  React.useEffect(() => {
    if (rating) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [rating]);

  React.useEffect(() => {
    if (childRef.current) {
      const parentElement = childRef.current.parentElement;
      if (parentElement) {
        if (open) {
          parentElement.classList.add("sticky", "bottom-6", "z-[1000]");
        } else {
          parentElement.classList.remove("sticky", "bottom-6", "z-[1000]");
        }
      }
    }
  }, [open]);

  return (
    <>
      <div ref={childRef} className={cn("flex justify-center h-12")}>
        <div
          ref={feedbackRef}
          className={cn(
            "border border-greyscale-30 rounded-8 relative z-[1000] overflow-hidden",
            {
              "bg-background/95 backdrop-blur h-fit w-96": open,
              "-translate-y-[calc(100%-48px)] shadow-100": open,
              "bg-background h-12 w-fit": !open,
            },
          )}
        >
          <form onSubmit={handleSubmit(submitFeedback)} id="feedbackForm">
            <div
              className={cn("flex items-center justify-center px-4 gap-2", {
                "py-4": open,
                "py-2": !open,
              })}
            >
              <p className="text-sm">{t("feedback:popup.title")}</p>
              <ToggleGroup.Root
                className="flex items-center gap-1"
                type="single"
                value={rating}
                onValueChange={(value) => {
                  setRating(value);
                  setValue("rating", value);
                }}
              >
                <ToggleGroup.Item
                  value="1"
                  className="flex items-center justify-center w-8 h-8 rounded-full text-red hover:bg-status-light-error hover:text-red data-[state=on]:bg-status-light-error data-[state=on]:text-red border-2 border-transparent data-[state=on]:border-status-bold-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                >
                  <Icon name="face-frown" className="w-4 h-4" />
                </ToggleGroup.Item>
                <ToggleGroup.Item
                  value="2"
                  className="flex items-center justify-center w-8 h-8 rounded-full text-amber hover:bg-status-light-warning hover:text-amber data-[state=on]:bg-status-light-warning data-[state=on]:text-amber border-2 border-transparent data-[state=on]:border-status-bold-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                >
                  <Icon name="face-neutral" className="w-4 h-4" />
                </ToggleGroup.Item>
                <ToggleGroup.Item
                  value="3"
                  className="flex items-center justify-center w-8 h-8 rounded-full text-green hover:bg-status-light-success hover:text-green data-[state=on]:bg-status-light-success data-[state=on]:text-green border-2 border-transparent data-[state=on]:border-status-bold-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                >
                  <Icon name="face-happy" className="w-4 h-4" />
                </ToggleGroup.Item>
              </ToggleGroup.Root>
              <input
                type="hidden"
                {...register("rating", { required: true })}
                defaultValue={rating}
              />
            </div>
            {open && (
              <>
                <div>
                  <div className="flex flex-col p2-2 pb-4 px-4 gap-2">
                    <div className="space-y-2">
                      <div className="fui-form-control">
                        <Controller
                          name="content"
                          control={control}
                          rules={{ required: true }}
                          render={({ field: { ref, ...rest } }) => (
                            <TextArea
                              {...rest}
                              ref={(el) => {
                                ref(el);
                                // @ts-ignore (complains current is read-only)
                                contentRef.current = el;
                              }}
                              id="content"
                              className="!resize-none"
                              placeholder={t("feedback:popup.placeholder")}
                              invalid={!!errors.content}
                            />
                          )}
                        />
                      </div>
                      <p className="text-xxs text-balance text-right">
                        {t("feedback:popup.disclaimer")}
                      </p>
                    </div>
                  </div>
                  <div className="py-4 px-4 border-t-0 pt-2 border-t-greyscale-30">
                    <div className="flex items-center justify-end gap-x-4">
                      <Button
                        fullWidth
                        variant="outline"
                        onClick={() => setOpen(false)}
                      >
                        {t("feedback:popup.cancel")}
                      </Button>
                      <Button
                        type="submit"
                        loading={status === "pending"}
                        fullWidth
                      >
                        {t("feedback:popup.send_feedback")}
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </form>
        </div>
      </div>
    </>
  );
};
